/**
 * Every API call goes through here so credentials handling is decided once.
 *
 * `credentials: 'include'` is what carries the httpOnly session cookie. Vite
 * proxies /api to Fastify in dev and Caddy does the same in production, so the
 * request is same-origin in both — which is what makes SameSite=Lax work.
 */
export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function readMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const record = body as Record<string, unknown>;
  if (typeof record.error === 'string') return record.error;
  if (typeof record.message === 'string') return record.message;
  return null;
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // Sent on every request so the server can downgrade the effective role. It is
  // advisory only — the server checks it against the session and never upgrades.
  const viewAs =
    typeof localStorage === 'undefined' ? null : localStorage.getItem('greengrocer:view-as');

  /*
   * Content-Type is set only when there is actually a body to describe.
   *
   * Sending `application/json` on a bodyless request makes Fastify's JSON parser
   * reject it with "Body cannot be empty when content-type is set to
   * 'application/json'" — a 400 before the route is ever reached. That silently
   * broke every DELETE in the application: removing a product from a store,
   * unassigning an admin, deleting a store. The header is a description of the
   * body, so a request without one must not claim to have any.
   */
  const hasBody = init?.body !== undefined && init.body !== null;

  const response = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(viewAs ? { 'X-View-As': viewAs } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    // Two shapes in play: our own routes answer {error}, while Better Auth's
    // endpoints answer {message, code}. Reading only one of them showed users a
    // bare "Request failed with status 401" instead of "Invalid username or
    // password" on the single screen where the wording matters most.
    const message = readMessage(body) ?? `Request failed with status ${response.status}`;
    throw new ApiError(message, response.status);
  }

  return response.json() as Promise<T>;
}
